import React, { useEffect, useState } from "react";
import { styled } from "styletron-react";
import {
  FaFileArchive,
  FaFileImage,
  FaFilePdf,
  FaFolder,
  FaDownload,
  FaTrash,
} from "react-icons/fa";
import { connect } from "react-redux";
import {
  deleteModelRunAsset,
  fetchModelRunById,
} from "../../redux/model-run/ModelRunThunk";
import LoadingOverlay from "../../components/loading-overlay/LoadingOverlayComponent";
import { useParams } from "react-router-dom";
import * as GlobalConstants from "../../constants/GlobalConstants";
import useFileDownloader from "../../hooks/useFileDownloader";
import useToaster from "../../hooks/useToaster";
import EmptyListNotificationComponent from "../../components/empty-list-notification/EmptyListNotificationComponent";
import useRouteParams from "../../hooks/useRouteParams";
import { fetchModelRunAssets } from "../../redux/model-run/ModelRunThunk";

const Container = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  overflowY: "auto",
  gap: "16px",
});

const Thumbnail = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "180px",
  padding: "8px",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  borderRadius: "4px",
  position: "relative",
});

const FileName = styled("div", {
  marginTop: "8px",
  textAlign: "center",
  wordWrap: "break-word",
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const FileActions = styled("div", {
  position: "relative",
  display: "flex",
  margin: "8px 8px 0 8px",
  padding: "8px",
  justifyContent: "space-between",
  width: "100%",
});

const ICON_SIZE = 90;
const MAX_FILE_NAME_LENGTH = 20;

const ModelerDocumentationView = (props) => {
  const { modelerId } = useParams();
  const [downloadFile, loading] = useFileDownloader(true);
  const { _, searchParams, searchPart } = useRouteParams();

  useToaster(props.deleteErrorMessage);

  useEffect(() => {
    if (searchParams["runId"]) {
      props.onFetchModelRunAssets(
        modelerId,
        searchParams["runId"],
        100,
        null
      );
    }
  }, [searchParams.runId]);

  const truncateFileName = (name) => {
    if (name.length <= MAX_FILE_NAME_LENGTH) return name;
    return `${name.slice(0, MAX_FILE_NAME_LENGTH - 3)}...`;
  };

  const handleDownloadClick = (asset) => {
    if (!loading) {
      downloadFile("modeler", `${modelerId}/${asset.name}`, `${asset.filename}`);
    }
  };

  const handleDeleteClick = (asset) => {
    if (!loading || !props.deleteInProgress) {
      props.onDeleteModelRunAsset(asset.id);
    }
  };

  const getIconStyle = () => ({
    cursor: loading || props.deleteInProgress ? "not-allowed" : "pointer",
    opacity: loading || props.deleteInProgress ? 0.5 : 1,
  });

  const getIcon = (file) => {
    const iconMap = {
      folder: <FaFolder size={ICON_SIZE} />,
      PICTURES: <FaFileImage size={ICON_SIZE} />,
      ARCHIVES: <FaFileArchive size={ICON_SIZE} />,
      DOCUMENTS: <FaFilePdf size={ICON_SIZE} />,
    };

    return iconMap[file.type] ? (
      <Thumbnail style={{ ...getIconStyle() }} key={file.name}>
        <FileActions>
          <FaDownload
            size={28}
            style={{ marginRight: "8px", ...getIconStyle() }}
            onClick={() => handleDownloadClick(file)}
            disabled={loading || props.deleteInProgress}
          />
          <FaTrash
            size={20}
            style={{ ...getIconStyle() }}
            onClick={() => handleDeleteClick(file)}
            disabled={loading || props.deleteInProgress}
          />
        </FileActions>
        {iconMap[file.type]}
        <FileName
          $style={{ marginTop: "8px", marginBottom: "8px" }}
          title={file.filename}
        >
          {truncateFileName(file.filename)}
        </FileName>
      </Thumbnail>
    ) : null;
  };

  return (
    <Container>
      <LoadingOverlay
        isLoading={
          props.deleteModelRunStatus === GlobalConstants.REQUEST_STATUS.STARTED ||
          props.fetchInProgress
        }
      />
      {props.assets.length > 0 ? (
        props.assets.map(getIcon)
      ) : (
        <EmptyListNotificationComponent
          message="No attachments found for this run. Please use the 'Upload' button to add run-specific results or images."
        />
      )}
    </Container>
  );
};

const mapStateToProps = (state) => ({
  assets: state.modelRun?.selectedModelRun?.assets || [],
  fetchInProgress: state.modelRun?.fetchModelRunAssetsInProgress,
  deleteInProgress: state.modelRun?.deleteAssetInProgress,
  deleteAssetStatus: state.modelRun?.deleteAssetStatus,
  deleteErrorMessage: state.modelRun?.deleteErrorMessage,
});

const mapDispatchToProps = (dispatch) => ({
  onFetchModelRunAssets: (modelerId, modelRunId, page_size, nextToken) => {
    dispatch(fetchModelRunAssets(modelerId, modelRunId, page_size, nextToken));
  },
  onDeleteModelRunAsset: (assetId) => dispatch(deleteModelRunAsset(assetId)),
  onFetchModelRunById: (modelRunId) => dispatch(fetchModelRunById(modelRunId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ModelerDocumentationView);